/**
 * Restores the src/srcset that `product-media.liquid`'s `defer_fetch` parked in
 * data-* attributes, the first time the container holding them is shown.
 *
 * Why this exists: `loading="lazy"` does not defer an image inside a container that
 * starts `display: none`. Such an element has no layout box, so the browser cannot
 * place it relative to the viewport and fetches it immediately. On a PDP that meant
 * the inactive product-selection tab (1.4 MB) and the closed "elevate your style"
 * dialog (0.9 MB) downloading every card image on arrival, for panels the shopper
 * may never open.
 *
 * The unit is the container, not the image. A shopper who opens a tab or a drawer is
 * going to browse it, so the whole strip is fetched at once rather than card by card
 * as it is scrolled — no images arriving under the thumb mid-swipe.
 *
 * IntersectionObserver is the trigger precisely because of the `display: none`: such a
 * container never intersects, and the observer fires on its own the moment something
 * gives it a box. So nothing has to tell this module that a panel opened — not the tab
 * component, not the dialog, not a future container.
 */

const CONTAINER = '[data-deferred-images]';

/** @param {Element} container */
function reveal(container) {
  container.removeAttribute('data-deferred-images');

  for (const img of container.querySelectorAll('img[data-src], img[data-srcset]')) {
    if (!(img instanceof HTMLImageElement)) continue;
    const { src, srcset } = img.dataset;
    // srcset before src: with both present the browser chooses from srcset, so setting
    // src first would start a fetch of the placeholder's replacement at one resolution
    // and then discard it.
    if (srcset) {
      img.srcset = srcset;
      delete img.dataset.srcset;
    }
    if (src) {
      img.src = src;
      delete img.dataset.src;
    }
    // The shopper just opened the thing this image is in — it is not background work.
    img.fetchPriority = 'high';
  }
}

const observer = new IntersectionObserver((entries) => {
  for (const entry of entries) {
    if (!entry.isIntersecting) continue;
    observer.unobserve(entry.target);
    reveal(entry.target);
  }
});

/**
 * @param {ParentNode} root
 * @returns {number} how many deferring containers were found
 */
function watch(root) {
  const containers = root.querySelectorAll(CONTAINER);
  for (const container of containers) observer.observe(container);
  return containers.length;
}

const deferredAtStart = watch(document);

/* Panels can also arrive after load — a section re-render, the recently-viewed strip
   filling itself in. Only worth watching for on a page that defers anything at all:
   this module is global, and a document-wide childList observer on a collection page
   with nothing deferred would be pure overhead. Modules are deferred, so the document
   is already parsed here and that count is final. */
if (deferredAtStart > 0) {
  new MutationObserver((records) => {
    for (const record of records) {
      for (const node of record.addedNodes) {
        if (!(node instanceof Element)) continue;
        if (node.matches(CONTAINER)) observer.observe(node);
        else watch(node);
      }
    }
  }).observe(document.documentElement, { childList: true, subtree: true });
}
